<script lang="ts">
  import { getPlaceholderCreationFailures, uploads } from "state/upload";
  import { getAllEditedImages, getCurrentImage, getSelectedIds } from "./editImageMachine";

  import { getQueryClientContext } from "@tanstack/svelte-query";
  import EditPreview from "./EditPreview.svelte";
  import EditForm from "./EditForm.svelte";
  import { orgCheck } from "@nrk/origo";
  import { onDestroy } from "svelte";
  import { editActor, editState, initEditSession, teardownEditSession } from "./editSession";
  import type { EditImageActor, EditImageState } from "./editImageMachine";
  import { isPlugin } from "state/app";

  export let itemIds: string[];
  export let onCancel: () => void;
  export let onDone: () => void;

  initEditSession({ itemIds, queryClient: getQueryClientContext(), onDone });
  onDestroy(teardownEditSession);

  // editActor and editState are always set before the template renders since initEditSession runs synchronously
  $: actor = $editActor as EditImageActor;
  $: state = $editState as EditImageState;

  $: images = $editState ? getAllEditedImages($editState) : [];
  $: selectedImage = $editState ? getCurrentImage($editState) : undefined;
  $: selectedIds = $editState ? getSelectedIds($editState) : [];

  function saveMetadata() {
    actor?.send({ type: "SUBMIT" });
  }

  function selectImage(imageId: string) {
    actor?.send({ type: "SELECT_IMAGE", imageId });
  }

  function selectMultipleImages(imageId: string) {
    actor?.send({ type: "SELECT_MULTIPLE_IMAGES", imageId });
  }
</script>

<div class="edit-container" class:standalone={!isPlugin}>
  {#if !$editState || $editState.matches("Loading")}
    <div class="org-spinner">
      <p>Henter metadata&hellip;</p>
    </div>
  {:else if $editState.matches("Error")}
    <div class="error">
      <h2>Noe gikk galt</h2>
      <p>
        <em>{$editState.context.error?.message || "Ukjent feil"}</em>
      </p>
      <p>
        <button class="org-button org-button--secondary" on:click={onCancel}>Avbryt</button>
      </p>
    </div>
  {:else if images.length}
    <div class="content-container">
      <div class="image-column">
        {#if $editState.context.images.length > 1}
          <div class="edit-infobar org-bar org-bar--secondary">
            <i
              >{$editState.context.selectedIds.length === $editState.context.images.length
                ? "Alle"
                : `${$editState.context.selectedIds.length} av ${$editState.context.images.length}`}
              bilder valgt</i
            >
            <hr />
            <button
              class="org-button"
              type="button"
              disabled={$editState.context.selectedIds.length === $editState.context.images.length}
              on:click|preventDefault={() => actor?.send({ type: "SELECT_ALL" })}
            >
              {@html orgCheck} Marker alle
            </button>
          </div>
        {/if}
        <div class="tiles-container">
          {#each images as image (image.id)}
            <EditPreview
              {state}
              {image}
              selected={!$editState.matches("Saving") &&
                ($editState.matches("Editing.Multiple") || selectedIds.includes(image.id))}
              onSelect={() => selectImage(image.id)}
              onSelectMulti={() => selectMultipleImages(image.id)}
            />
          {/each}
          {#each getPlaceholderCreationFailures($uploads) as file (file.file.name)}
            <EditPreview
              image={{ originalFileName: file.file.name }}
              onSelect={() => {}}
              onSelectMulti={() => {}}
              selected={false}
              {state}
            />
          {/each}
        </div>
      </div>

      <section class="edit-form">
        {#if selectedImage}
          {#key selectedImage.id}
            <EditForm service={actor} {state} image={selectedImage} {saveMetadata} />
          {/key}
        {/if}
      </section>
    </div>
  {/if}
</div>

<style>
  .edit-infobar {
    background-color: var(--color-surface-primary);
    color: var(--color-text-primary);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .tiles-container {
    display: flex;
    flex-direction: column;
    gap: var(--org-small);
    flex: 1;
    overflow-y: auto;
  }
  .edit-container {
    display: flex;
    flex-flow: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    flex-grow: 1;
    overflow: hidden;
  }

  .error {
    margin: var(--org-medium) var(--org-large);
  }

  .org-spinner {
    padding: 4rem;
    text-align: center;
  }

  .org-spinner p {
    margin-top: 8rem;
  }

  .content-container {
    display: flex;
    width: 100%;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .edit-form {
    overflow-y: auto;
    background-color: var(--color-surface-tertiary);
    height: 100%;
    min-height: 0;
  }

  .image-column {
    display: flex;
    flex-direction: column;
    padding-left: var(--org-xsmall);
  }

  @media (min-width: 1200px) {
    .edit-form {
      flex: 0 0 34%;
    }
    .image-column {
      flex: 0 0 66%;
    }
  }
  @media (max-width: 1199px) {
    .edit-form {
      flex: 0 0 50%;
    }
    .image-column {
      flex: 0 0 50%;
    }
  }

  @media (max-width: 768px) {
    .standalone .content-container {
      flex-direction: column;
      height: auto;
    }
    .standalone .edit-form {
      min-width: 0;
      flex-basis: auto;
      overflow-y: visible;
    }
    .standalone .image-column {
      max-height: 55%;
      min-height: 220px;
    }
  }
</style>
